<template>
  <div
    v-if="hasContent"
    class="message-bubble-wrap"
    :class="[
      side === 'me' ? 'message-bubble-wrap--me' : 'message-bubble-wrap--them',
      { 'message-bubble-wrap--group-start': groupStart },
    ]"
  >
    <!-- The author line. Multi-party surfaces only, and only on the first
         bubble of a run — a consecutive block from one person reads as one
         turn. A 1:1 thread leaves `showAuthor` off and never draws it. -->
    <span
      v-if="showAuthor && groupStart"
      class="message-bubble__author"
      :class="[
        role ? `message-bubble__author--${role}` : null,
        { 'message-bubble__author--me': side === 'me' },
      ]"
    ><span class="message-bubble__author-name">{{ side === 'me' ? selfLabel : authorName }}</span><span
      v-if="roleLabel"
      class="message-bubble__author-role"
    >{{ ' ' }}{{ roleLabel }}</span></span>

    <div
      class="message-bubble"
      :class="[
        `message-bubble--${side}`,
        kindModifier,
        role && side !== 'me' ? `message-bubble--role-${role}` : null,
      ]"
    >
      <p v-if="message.kind === 'text'" class="message-bubble__text">{{ message.body }}</p>

      <!-- A control only when there is a vehicle to open. An unresolved
           reference is a WAIT rather than a broken button — the consumer
           hydrates it asynchronously — so it renders as a plain element with no
           name and no tab stop, instead of a disabled button announcing an
           action that was never offered. -->
      <component
        :is="openable ? 'button' : 'div'"
        v-else-if="message.kind === 'vehicle'"
        class="message-bubble__vehicle"
        :class="{ 'message-bubble__vehicle--openable': openable }"
        :type="openable ? 'button' : null"
        :aria-label="openable ? assetCardLabel : null"
        @click="openVehicle"
      >
        <div class="message-bubble__asset-card">
          <div class="message-bubble__asset-card-media">
            <img
              v-if="vehicle?.image"
              :src="vehicle.image"
              :alt="vehicleTitle(vehicle)"
            >
            <div v-else class="message-bubble__asset-card-media-fallback" />
            <span
              v-if="countdown"
              class="message-bubble__asset-card-chip"
            >{{ countdown }}</span>
          </div>
          <!-- The spaces between the card's parts draw nothing, between blocks.
               They are there for whatever reads the card as text, which would
               otherwise run "2 DAYS" into the year and the title into the price. -->
          {{ ' ' }}
          <div class="message-bubble__asset-card-body">
            <div class="message-bubble__asset-card-title">{{ vehicleTitle(vehicle) }}</div>
            {{ ' ' }}
            <div v-if="vehicle?.price != null" class="message-bubble__asset-card-price">{{ priceLabel(vehicle?.price) }}</div>
          </div>
        </div>
      </component>

      <!-- An offer move on the thread. The card is the whole bubble, so it is
           the text bubble's fill and corners at a fixed width rather than a
           card inset in one the way the vehicle is. An offer message that
           arrived without its payload falls through to the body below. -->
      <template v-else-if="isOffer">
        <div class="message-bubble__offer-body">
          <!-- Which car the number is about. A thread belongs to a PAIR of
               people rather than to one car, so a second offer to the same
               seller is negotiated in the thread the first one opened — and a
               card that named no asset left that offer unreadable.
               It is a control for the same reason the vehicle card is one: the
               asset resolved, so there is somewhere to go. The thumb is
               decorative, because the chip says the car in words beside it. -->
          <component
            :is="chipOpenable ? 'button' : 'div'"
            v-if="showOfferChip && offerChipShown"
            :type="chipOpenable ? 'button' : null"
            class="message-bubble__offer-chip"
            :class="{ 'message-bubble__offer-chip--static': !chipOpenable }"
            :aria-label="chipOpenable ? offerAssetLabel : null"
            @click="openVehicle"
          >
            <!-- A thumb that fails to load falls back to the empty tile
                 rather than the browser's broken-image glyph. The chip with no
                 asset to open takes the car icon instead of a photo: what it
                 names is what the offer kept, not a car anything can show. -->
            <img
              v-if="chipOpenable && vehicle.image && failedThumb !== vehicle.image"
              class="message-bubble__offer-chip-thumb"
              :src="vehicle.image"
              alt=""
              @error="failedThumb = vehicle.image"
            >
            <span
              v-else
              class="message-bubble__offer-chip-thumb message-bubble__offer-chip-thumb--empty"
            ><i
              v-if="!chipOpenable"
              class="pi pi-car message-bubble__offer-chip-icon"
              aria-hidden="true"
            /></span>
            <span class="message-bubble__offer-chip-title">{{ offerChipTitle }}</span>
          </component>

          <div
            class="message-bubble__offer-label"
            :class="{ 'message-bubble__offer-label--accepted': isAccepted }"
          ><template v-if="offerLabelUnits.tail"><span
              class="message-bubble__offer-label-unit message-bubble__offer-label-unit--name"
            >{{ offerLabelUnits.head }}</span>{{ ' ' }}<span
              class="message-bubble__offer-label-unit"
            >{{ offerLabelUnits.tail }}</span></template><template
              v-else
            >{{ offerLabelText.before }}</template></div>
          <div
            class="message-bubble__offer-amount"
            :class="{ 'message-bubble__offer-amount--replaced': replaced }"
          >{{ formatPrice(offer.amount) }}<span
            v-if="offerLabelText.after"
            class="message-bubble__offer-label-after"
          >{{ ' ' }}{{ offerLabelText.after }}</span></div>
          <span
            v-if="replaced"
            class="message-bubble__visually-hidden"
          >{{ replacedLabel }}</span>
          <!-- Where this offer stands. Every card says it, whatever car the
               offer is on: the card above the thread speaks for the pinned
               car's offer alone, so a declined or withdrawn offer on any other
               car had nothing saying so. -->
          <!-- A move a later counter replaced says no status at all: only the
               latest move speaks for the offer, so an older one can never read
               as final. Its number goes quiet instead, and a
               screen reader hears why. -->
          <div v-if="statusLabel && !replaced" class="message-bubble__offer-status">{{ statusLabel }}</div>
          <div v-if="offer.note" class="message-bubble__offer-sub">{{ offer.note }}</div>
        </div>

        <div v-if="showCounterActions" class="message-bubble__offer-actions">
          <button
            type="button"
            class="message-bubble__offer-action message-bubble__offer-action--accept"
            @click="offerAction('accept')"
          >{{ acceptLabel }}</button>
          <button
            type="button"
            class="message-bubble__offer-action message-bubble__offer-action--counter"
            @click="offerAction('counter')"
          >{{ counterLabel }}</button>
        </div>

        <div v-else-if="isAccepted && !replaced" class="message-bubble__offer-actions">
          <button
            type="button"
            class="message-bubble__offer-action message-bubble__offer-action--accept"
            @click="offerAction('finalize')"
          >{{ finalizeLabel }}</button>
        </div>
      </template>

      <p v-else class="message-bubble__text">{{ message.body }}</p>
    </div>

    <div class="message-bubble__foot">
      <span class="message-bubble__time">{{ message.timeLabel }}</span>
      <!-- A font glyph carries no text at all, so "read" was available to
           sighted users only. `role` goes with the label because `aria-label`
           on a generic element is not reliably exposed. English, like the
           library's other accessible names — see the Storybook description. -->
      <i
        v-if="side === 'me' && message.readReceipt"
        class="pi pi-check message-bubble__receipt"
        role="img"
        aria-label="Read"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { formatPrice } from '../../utils/formatters';

// One message, on every surface that has a chat.
//
// It serves two conversation shapes, and the difference between them is two
// props rather than two components:
//
//   1:1 (the mobile app)   — `showAuthor` off. Two sides, `me` and `them`, and
//                            nothing identifies the speaker because there is
//                            only one of them. This is the default, so the
//                            mobile call sites pass nothing new.
//   multi-party (the lane) — `showAuthor` on and a `role` per message. The
//                            author's name leads each run and tints with their
//                            role, because a clerk needs to know at a glance
//                            whether a line came from the auctioneer or a
//                            buyer.
//
// `groupStart` is presentation, not data: it needs the NEIGHBORING message, so
// it cannot be decided per-bubble, and the list that has both decides it. The
// bubble renders the answer.
//
// The timestamp sits in the foot on both surfaces. The lane chat used to put it
// in a head line beside the author; it moved here when the two bubbles merged,
// so one component has one answer for where the time goes.
defineOptions({ name: 'MessageBubble' });

const props = defineProps({
  // { kind: 'text' | 'vehicle' | 'offer', author: 'me' | 'them', body,
  //   timeLabel, readReceipt, authorName, role, offer }. `role` is read only
  //   when `showAuthor` is set; `authorName` is read there and in an offer
  //   card's label ("Marcus countered").
  //
  //   `offer` rides only on an `offer` message:
  //   { id, state: 'pending' | 'countered' | 'accepted' | …, amount, note }.
  //   `note` is the optional sub line under the amount.
  message: { type: Object, required: true },
  // Resolved asset for `vehicle` bubbles (from the liked store's asset bank).
  vehicle: { type: Object, default: null },
  // Pre-computed auction countdown label for the vehicle bubble.
  countdown: { type: String, default: '' },
  // Draw the author line. Off for a 1:1 thread, on for a multi-party channel.
  showAuthor: { type: Boolean, default: false },
  // First bubble of a run from this author — see the note above.
  groupStart: { type: Boolean, default: false },
  // What the viewer's own messages are called on the author line.
  selfLabel: { type: String, default: 'You' },
  // This offer is the newest counter from the other side. Like `groupStart`
  // it needs the rest of the thread, so the list decides it: only that
  // counter carries Accept / Counter, and an earlier one it superseded is
  // drawn with none.
  latestCounter: { type: Boolean, default: false },
  // An offer bubble's words, as English defaults, so a consumer that passes
  // nothing reads exactly as it did. `offeredLabel` and `counteredLabel` are
  // whole credits rather than a verb glued to `selfLabel`: French says "Vous
  // avez offert" for the viewer and "Contre-offre de Marcus" for the other
  // side, and neither is the other with a name swapped in. `{name}` stands for
  // whoever moved. Null means "keep the English the card composes itself".
  // The card's credit in the consumer's words, whole, as the page passes it: "{name} :
  // contre‑offre {amount}". `{amount}` marks where the figure stands; the label
  // draws the words before it (the amount line draws the figure). It wins over
  // `offeredLabel` / `counteredLabel`, which stay for a consumer that cannot
  // tell a buyer's counter from a first offer. Null keeps those.
  offerLabel: { type: String, default: null },
  offeredLabel: { type: String, default: null },
  counteredLabel: { type: String, default: null },
  // The accepted card's credit, the viewer's and the other side's (`{name}`):
  // French conjugates it ("Vous avez accepté", "Marcus a accepté"), so it is
  // two whole credits too. English as before when absent.
  selfAcceptedLabel: { type: String, default: null },
  acceptedLabel: { type: String, default: null },
  // Draw the asset chip on an offer card. The list decides, since it needs
  // the whole thread: a thread whose every offer is on its pinned car leaves
  // the chip off, because the card above the thread already names that car.
  showOfferChip: { type: Boolean, default: true },
  // What the offer is on, as the OFFER kept it: { title }. It is what the chip
  // names when `vehicle` has not resolved — a deleted or unreachable asset
  // doc — so the card still says a car rather than reading as the thread's
  // pinned one. A chip built from it is not a control: there is
  // no asset to open.
  offerAsset: { type: Object, default: null },
  // Where the offer stands, in the consumer's words ("Pending", "Declined").
  // Every offer card says it, whatever car the offer is on.
  statusLabel: { type: String, default: '' },
  // A later move replaced this one: a counter answered it. The list decides,
  // since it needs the whole negotiation. A replaced move draws no status and
  // no actions, and its number takes the muted tone, because only the latest
  // move says where the offer stands. `replacedLabel` is what a
  // screen reader hears after the number; English default, the app passes its
  // own.
  replaced: { type: Boolean, default: false },
  replacedLabel: { type: String, default: 'replaced by a counter-offer' },
  // What a car the consumer could not name is called. `assetFallbackLabel` is
  // the accessible name of a chip whose asset resolved with no title;
  // `assetUnavailableLabel` is the words on a chip with nothing to name at
  // all. English defaults, as the library's other names are, and the app
  // passes its own translated ones.
  assetFallbackLabel: { type: String, default: 'Vehicle' },
  assetUnavailableLabel: { type: String, default: 'Vehicle unavailable' },
  // The card's buttons, English defaults; the app passes its own.
  acceptLabel: { type: String, default: 'Accept' },
  counterLabel: { type: String, default: 'Counter' },
  finalizeLabel: { type: String, default: 'Finalize purchase' },
});

// The thumb URL that failed to load, so the chip draws the empty tile for it.
// Keyed by URL, so a new image for the chip is tried afresh.
const failedThumb = ref(null);

// Reduced scope: text, vehicle-reference and offer bubbles — no image or
// wishlist bubbles. `wishlist` there is the attachment kind on the wire, not
// the retired product word.
const emit = defineEmits(['open-vehicle', 'offer-action']);

// `author` is the side the bubble sits on and has only ever had two values.
// Anything that is not the viewer is `them`, so a multi-party channel can pass
// a real name through `authorName` without it becoming a CSS class.
const side = computed(() => (props.message.author === 'me' ? 'me' : 'them'));
const authorName = computed(() => props.message.authorName ?? '');

// `buyer` and `watcher` share the neutral treatment — they are separate roles
// to the sale, not to the eye — so neither declares a tint below.
const role = computed(() => props.message.role ?? '');

// The role in words, because a tint is color and only color: a screen reader
// announces nothing of it, and neither does a lane read in grayscale or by
// someone who cannot separate the amber from the blue. The word is what makes
// the role survive all three, and it is the same channel in every case rather
// than a glyph for the eye and a label for the reader.
//
// It also closes the wider half of the gap. `buyer` and `watcher` declare no
// tint at all, so they were distinguishable from each other by NOTHING.
//
// The console's own words, taken from ChatPanel's role table rather than
// invented here, and `rep` spelled out because this repository does not
// abbreviate in copy.
const ROLE_LABELS = Object.freeze({
  auctioneer: 'Auctioneer',
  rep: 'Selling representative',
  clerk: 'Clerk',
  buyer: 'Buyer',
  watcher: 'Watcher',
});

// `Object.hasOwn` rather than a bare lookup: `role` is free text off a message,
// so a value colliding with something on Object's prototype would otherwise
// resolve to a function and render as one.
//
// The viewer's own line names no role. There is no color signal there to
// compensate for — it is never role-tinted — and the reader knows who they are.
const roleLabel = computed(() =>
  side.value !== 'me' && Object.hasOwn(ROLE_LABELS, role.value) ? ROLE_LABELS[role.value] : ''
);

const vehicleTitle = (v) =>
  v?.title || [v?.year, v?.make, v?.model].filter(Boolean).join(' ');
const priceLabel = (n) => formatPrice(n, '');

// Whether there is anything to draw at all — the guard on the ROOT, so a message
// this component cannot draw renders nothing rather than an empty bubble. It has
// to be the whole component and not the bubble alone: hiding only the bubble
// leaves the foot behind as a naked timestamp floating in the stream.
//
// A vehicle bubble always has its card and an offer bubble has one when its
// payload came with it; every other kind is its body, and the store sends
// `image` and `wishlist` with that body null.
const offer = computed(() => props.message.offer ?? null);
const isOffer = computed(() => props.message.kind === 'offer' && offer.value != null);

// An offer that arrived without its payload draws as its body, so it takes no
// card modifier either — the card's fixed width would squeeze the text.
const kindModifier = computed(() => {
  const { kind } = props.message;
  if (!kind || (kind === 'offer' && !isOffer.value)) return null;
  return `message-bubble--${kind}`;
});

const hasContent = computed(
  () =>
    props.message.kind === 'vehicle' ||
    isOffer.value ||
    String(props.message.body ?? '').trim().length > 0
);

// The card's label names who moved. The other side is the name the message
// carries, and "Seller" when it carries none, as OfferHistoryList has it.
const isAccepted = computed(() => offer.value?.state === 'accepted');
const composedLabel = computed(() => {
  const who = side.value === 'me' ? props.selfLabel : authorName.value || 'Seller';
  if (props.offerLabel) return props.offerLabel.split('{amount}')[0].trimEnd().replace('{name}', who);
  if (isAccepted.value) {
    const accepted = side.value === 'me' ? props.selfAcceptedLabel : props.acceptedLabel;
    return accepted ? accepted.replace('{name}', who) : `${who} accepted`;
  }
  const credit = side.value === 'me' ? props.offeredLabel : props.counteredLabel;
  if (credit) return credit.replace('{name}', who);
  return side.value === 'me' ? `${who} offered` : `${who} countered`;
});

// The credit split at its figure: the words before `{amount}` are the label
// (composed above, with the name filled in), and any words after it follow
// the figure on the amount line.
const offerLabelText = computed(() => {
  const after = props.offerLabel
    ? props.offerLabel.split('{amount}').slice(1).join('').trim()
    : '';
  return { before: composedLabel.value, after };
});

// A credit with a colon ("Marcus Webb : contre‑offre", "Votre offre :")
// breaks only after it: the name and its colon come
// first, and what follows is one unbreakable unit. A name that fits the label
// is never broken, because it starts the line; one wider than the label
// ("Marie-Ève Bélanger-Tremblay") wraps at its spaces and hyphens rather than
// running out of the card. A credit with no colon wraps as
// words do.
const offerLabelUnits = computed(() => {
  const label = offerLabelText.value.before;
  const colon = label.indexOf(':');
  if (colon < 0 || colon === label.length - 1) return { head: label, tail: '' };
  return { head: label.slice(0, colon + 1), tail: label.slice(colon + 1).trim() };
});

// Accept / Counter answer the counter on the table, and only that one.
const showCounterActions = computed(
  () => side.value === 'them' && props.latestCounter && !props.replaced && offer.value?.state === 'countered'
);

function offerAction(action) {
  emit('offer-action', { action, offerId: offer.value.id });
}

// Whether an offer card has a car to name at all: the asset resolved, or the
// offer kept what it was made on. With neither, the card draws no chip.
const offerChipShown = computed(() => props.vehicle != null || props.offerAsset != null);

// The chip is a control only where the asset resolved — that is what gives it
// somewhere to go. Built from the offer's own record it is a plain row: not a
// button, no tab stop, no accessible name of its own, since the words it shows
// are all there is to say.
const chipOpenable = computed(() => props.vehicle != null);

// The car the chip names: the resolved asset's words, then the ones the offer
// kept, and last the consumer's word for a car it cannot name. A chip that
// opens something always reads as a car, so its last resort is the plain
// fallback rather than "unavailable".
const offerChipTitle = computed(() => {
  const kept = props.offerAsset?.title || '';
  if (chipOpenable.value) {
    return vehicleTitle(props.vehicle) || kept || props.assetFallbackLabel;
  }
  return kept || props.assetUnavailableLabel;
});

// The offer chip's accessible name is the car it opens, and nothing else: the
// chip shows the title and a decorative thumb, so the title is every word on
// it. The fallback covers an asset that resolved with nothing to call it.
const offerAssetLabel = computed(() => vehicleTitle(props.vehicle) || props.offerAsset?.title || props.assetFallbackLabel);

// Whether the card is a control. The reference resolving is what makes it one:
// the consumer looks the asset up and passes it in, so `null` means not yet.
const openable = computed(() => props.message.kind === 'vehicle' && props.vehicle != null);

// The button's accessible name is every word the card shows, in the order it
// shows them — the countdown chip, the heading, the price — so a person who
// speaks what they see to select it is heard, and nothing visible is left out
// of what a screen reader says. A name holding only the heading drops the price.
// The fallback covers an asset that resolved with nothing to call it, which
// would otherwise be a button with no name at all.
const assetCardLabel = computed(() => {
  const title = vehicleTitle(props.vehicle) || 'Shared vehicle';
  const price = props.vehicle?.price != null ? priceLabel(props.vehicle.price) : '';
  return [props.countdown, title, price].filter(Boolean).join(' ');
});

// The keyboard route is structural — the non-openable branch is not a button and
// takes no focus, so there is nothing to press. The pointer route still needs
// the guard, because a plain element receives a click either way.
function openVehicle() {
  if (props.vehicle) emit('open-vehicle', props.vehicle);
}
</script>

<style scoped>
.message-bubble-wrap {
  display: flex;
  flex-direction: column;
  max-width: 80%;
}

.message-bubble-wrap--me {
  align-self: flex-end;
  align-items: flex-end;
  max-width: 80%;
}

.message-bubble-wrap--them {
  align-self: flex-start;
  align-items: flex-start;
  max-width: 78%;
}

/* Only a multi-party surface groups runs, and only there does the extra space
   between runs mean anything — so this is inert until `groupStart` is passed. */
.message-bubble-wrap--group-start {
  margin-top: var(--p-message-bubble-group-start-margin-top);
}

.message-bubble__author {
  padding: var(--p-message-bubble-author-padding);
  font-size: var(--p-message-bubble-author-font-size);
  font-weight: var(--p-message-bubble-author-font-weight);
  color: var(--p-message-bubble-author-color);
}

/* The role word steps out of the role tint and back to the muted tone: the name
   carries the color, the word carries the meaning, and doubling the tint onto
   both would make the line read as one long tinted string. `margin` is
   positioning rather than theme, so it stays a literal. */
.message-bubble__author-role {
  margin-left: 2px;
  font-weight: var(--p-message-bubble-author-role-font-weight);
  color: var(--p-message-bubble-author-role-color);
}

.message-bubble__author--rep {
  color: var(--p-message-bubble-author-rep-color);
}

.message-bubble__author--auctioneer {
  color: var(--p-message-bubble-author-auctioneer-color);
}

/* "You" is never a role to identify — it is always the person reading — so the
   viewer's own name line steps back to the muted tone whatever role it carries. */
.message-bubble__author--me {
  color: var(--p-message-bubble-me-author-color);
}

.message-bubble {
  padding: var(--p-message-bubble-padding);
  border-radius: var(--p-message-bubble-border-radius);
  font-size: var(--p-message-bubble-font-size);
  line-height: var(--p-message-bubble-line-height);
  word-wrap: break-word;
}

.message-bubble--me {
  background: var(--p-message-bubble-me-background);
  color: var(--p-message-bubble-me-color);
  font-weight: var(--p-message-bubble-me-font-weight);
  border-bottom-right-radius: 5px;
}

.message-bubble--them {
  background: var(--p-message-bubble-them-background);
  color: var(--p-message-bubble-them-color);
  border-bottom-left-radius: 5px;
}

/* Role tints, `them` side only — the viewer's own bubble is already the brand
   fill and carries no role. */
.message-bubble--role-rep {
  background: var(--p-message-bubble-role-rep-background);
  color: var(--p-message-bubble-role-rep-color);
}

.message-bubble--role-auctioneer {
  background: var(--p-message-bubble-role-auctioneer-background);
  color: var(--p-message-bubble-role-auctioneer-color);
}

.message-bubble--role-clerk {
  background: var(--p-message-bubble-role-clerk-background);
  color: var(--p-message-bubble-role-clerk-color);
}

.message-bubble--vehicle {
  padding: var(--p-message-bubble-vehicle-padding);
  overflow: hidden;
}

.message-bubble__text {
  margin: 0;
  white-space: pre-wrap;
}

/* Vehicle card embedded inside a chat bubble.
   The openable branch is a <button>, so the block resets the UA button styling
   to what the plain element it replaced already had — measured, not guessed:
   240x216 at the same origin, block-level, no padding, no border, transparent,
   and text starting at the inline start rather than centered. The unopenable
   branch is a <div>, and every one of these is already its default there. */
.message-bubble__vehicle {
  display: block;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: start;
}

.message-bubble__vehicle--openable {
  cursor: pointer;
}

.message-bubble__asset-card {
  width: 240px;
  border-radius: var(--p-message-bubble-asset-card-border-radius);
  overflow: hidden;
  background: var(--p-message-bubble-asset-card-background);
}

.message-bubble--me .message-bubble__asset-card {
  background: var(--p-message-bubble-me-asset-card-background);
}

.message-bubble__asset-card-media {
  position: relative;
  aspect-ratio: 4 / 3;
  background: var(--p-message-bubble-asset-card-media-background);
}

.message-bubble__asset-card-media img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.message-bubble__asset-card-media-fallback {
  position: absolute;
  inset: 0;
  background: var(--p-message-bubble-asset-card-media-fallback-background);
}

.message-bubble__asset-card-chip {
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: var(--p-message-bubble-asset-card-chip-padding);
  border-radius: var(--p-message-bubble-asset-card-chip-border-radius);
  background: var(--p-message-bubble-asset-card-chip-background);
  color: var(--p-message-bubble-asset-card-chip-color);
  font-size: var(--p-message-bubble-asset-card-chip-font-size);
  font-weight: var(--p-message-bubble-asset-card-chip-font-weight);
  letter-spacing: var(--p-message-bubble-asset-card-chip-letter-spacing);
}

.message-bubble__asset-card-body {
  padding: var(--p-message-bubble-asset-card-body-padding);
  display: flex;
  flex-direction: column;
  gap: var(--p-message-bubble-asset-card-body-gap);
}

.message-bubble__asset-card-title {
  font-size: var(--p-message-bubble-asset-card-title-font-size);
  font-weight: var(--p-message-bubble-asset-card-title-font-weight);
  color: var(--p-message-bubble-asset-card-title-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-bubble--me .message-bubble__asset-card-title {
  color: var(--p-message-bubble-me-asset-card-title-color);
}

.message-bubble__asset-card-price {
  font-size: var(--p-message-bubble-asset-card-price-font-size);
  font-weight: var(--p-message-bubble-asset-card-price-font-weight);
  color: var(--p-message-bubble-asset-card-price-color);
}

.message-bubble--me .message-bubble__asset-card-price {
  color: var(--p-message-bubble-me-asset-card-price-color);
}

/* Offer card. The bubble keeps its side's fill and its asymmetric corner, and
   the padding moves inside so the actions row can sit closer to the edge than
   the figures do. `font-weight` resets the viewer's 500 text weight, which the
   card's sub line does not take. */
.message-bubble--offer {
  width: 240px;
  padding: var(--p-message-bubble-offer-padding);
  overflow: hidden;
  font-weight: var(--p-message-bubble-offer-font-weight);
}

.message-bubble__offer-body {
  padding: var(--p-message-bubble-offer-body-padding);
}

/* The car the offer is on, at the head of the card. The thumb is 28px, the
   smallest that still reads as a photo at the card's 240px width, and the title
   takes the rest of the row. The block resets the UA button styling to what a
   plain row would have had, as the vehicle card's branch does. The chip is the
   same opaque card on both sides, where the vehicle card goes translucent on
   the viewer's own: the 18% fill measures 3.97:1 under this title's 12px bold
   on light's green, and a chip is a control that has to stay legible.
   The title wraps to two lines rather than clipping, because the model is the
   part a clip cut, and the model is what tells two cars apart. The ::after
   hit-slop takes the 38px chip (40px with a two-line title) to a 44px tap
   target without changing its size: the 4px each way sits inside the body
   padding above and the margin below. The 38px is measured,
   and the target measures 44.5px. */
.message-bubble__offer-chip {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  margin: 0 0 8px;
  border: 1px solid var(--p-message-bubble-offer-chip-border-color);
  cursor: pointer;
  text-align: start;
  font: inherit;
  gap: var(--p-message-bubble-offer-chip-gap);
  padding: var(--p-message-bubble-offer-chip-padding);
  border-radius: var(--p-message-bubble-offer-chip-border-radius);
  background: var(--p-message-bubble-offer-chip-background);
}

.message-bubble__offer-chip::after {
  content: '';
  position: absolute;
  inset: -4px 0;
}

.message-bubble__offer-chip:focus-visible {
  outline: 2px solid var(--p-message-bubble-offer-chip-focus-outline-color);
  outline-offset: 2px;
}

.message-bubble--me .message-bubble__offer-chip:focus-visible {
  outline-color: var(--p-message-bubble-me-offer-chip-focus-outline-color);
}

.message-bubble__offer-chip-thumb {
  flex: none;
  width: 28px;
  height: 28px;
  object-fit: cover;
  border-radius: var(--p-message-bubble-offer-chip-thumb-border-radius);
}

/* The chip that names a car nothing can open: the same row, without a
   pointer, because there is nothing to press. */
.message-bubble__offer-chip--static {
  cursor: default;
}

.message-bubble__offer-chip-thumb--empty {
  display: grid;
  place-items: center;
  background: var(--p-message-bubble-offer-chip-thumb-empty-background);
}

/* Only the chip with no asset behind it draws the car: a resolved asset with
   no photo keeps the hollow tile it was blessed with. */
.message-bubble__offer-chip-icon {
  font-size: 14px;
  color: var(--p-message-bubble-offer-chip-icon-color);
}

.message-bubble__offer-chip-title {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.25;
  font-size: var(--p-message-bubble-offer-chip-title-font-size);
  font-weight: var(--p-message-bubble-offer-chip-title-font-weight);
  color: var(--p-message-bubble-offer-chip-title-color);
}

.message-bubble__offer-label {
  font-size: var(--p-message-bubble-offer-label-font-size);
  font-weight: var(--p-message-bubble-offer-label-font-weight);
  letter-spacing: var(--p-message-bubble-offer-label-letter-spacing);
  text-transform: uppercase;
  color: var(--p-message-bubble-offer-label-color);
}

.message-bubble__offer-label-unit {
  white-space: nowrap;
}

/* The name and its colon wrap as words do. The label is a fixed 212 px, so a
   name that fits starts the line and stays whole, and the break falls after
   the colon; a name wider than the label must wrap inside it, at its spaces
   and hyphens, or as a last resort anywhere, rather than overflow sideways.
   The NO-BREAK SPACE keeps the colon on the name's line. */
.message-bubble__offer-label-unit--name {
  white-space: normal;
  overflow-wrap: anywhere;
}

.message-bubble__offer-label--accepted {
  color: var(--p-message-bubble-offer-label-accepted-color);
}

/* After --accepted and more specific than it: the viewer's own card is the
   brand fill, so its label stays the dark ink whatever state it is in. */
.message-bubble--me .message-bubble__offer-label {
  color: var(--p-message-bubble-me-offer-label-color);
}

.message-bubble__offer-amount {
  margin-top: 2px;
  font-family: var(--p-message-bubble-offer-amount-font-family);
  font-size: var(--p-message-bubble-offer-amount-font-size);
  font-weight: var(--p-message-bubble-offer-amount-font-weight);
  font-variant-numeric: tabular-nums;
}

.message-bubble__offer-amount--replaced {
  color: var(--p-message-bubble-offer-amount-replaced-color);
  font-weight: var(--p-message-bubble-offer-amount-replaced-font-weight);
}

.message-bubble--me .message-bubble__offer-amount--replaced {
  color: var(--p-message-bubble-me-offer-amount-replaced-color);
}

.message-bubble__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.message-bubble__offer-status {
  margin-top: 2px;
  font-size: var(--p-message-bubble-offer-status-font-size);
  font-weight: var(--p-message-bubble-offer-status-font-weight);
  color: var(--p-message-bubble-offer-status-color);
}

.message-bubble--me .message-bubble__offer-status {
  color: var(--p-message-bubble-me-offer-status-color);
}

.message-bubble__offer-sub {
  margin-top: 2px;
  font-size: var(--p-message-bubble-offer-sub-font-size);
  line-height: var(--p-message-bubble-offer-sub-line-height);
  color: var(--p-message-bubble-offer-sub-color);
}

.message-bubble--me .message-bubble__offer-sub {
  color: var(--p-message-bubble-me-offer-sub-color);
}

.message-bubble__offer-actions {
  display: flex;
  gap: var(--p-message-bubble-offer-actions-gap);
  padding: var(--p-message-bubble-offer-actions-padding);
}

/* The phone's 44px floor, not the 40px height these carried. A native
   button, so the app's `.p-button` floor does not reach it. The clerk console's
   ChatPanel draws this component with no offer on it, so no desktop surface
   moves. */
.message-bubble__offer-action {
  flex: 1;
  min-height: 44px;
  cursor: pointer;
  border: none;
  border-radius: var(--p-message-bubble-offer-action-border-radius);
  font-family: var(--p-message-bubble-offer-action-font-family);
  font-size: var(--p-message-bubble-offer-action-font-size);
  font-weight: var(--p-message-bubble-offer-action-font-weight);
}

.message-bubble__offer-action:focus-visible {
  outline: 2px solid var(--p-message-bubble-offer-action-focus-outline-color);
  outline-offset: 2px;
}

.message-bubble__offer-action--accept {
  background: var(--p-message-bubble-offer-action-accept-background);
  color: var(--p-message-bubble-offer-action-accept-color);
}

.message-bubble__offer-action--accept:hover {
  background: var(--p-message-bubble-offer-action-accept-hover-background);
}

/* On the viewer's own bubble the accept fill would be the bubble's fill, so
   the button inverts there (Finalize purchase on "YOU ACCEPTED"). After the
   shared rules, at higher specificity, so neither order nor hover undoes it. */
.message-bubble--me .message-bubble__offer-action--accept {
  background: var(--p-message-bubble-me-offer-action-accept-background);
  color: var(--p-message-bubble-me-offer-action-accept-color);
}

.message-bubble--me .message-bubble__offer-action--accept:hover {
  background: var(--p-message-bubble-me-offer-action-accept-hover-background);
}

.message-bubble--me .message-bubble__offer-action:focus-visible {
  outline-color: var(--p-message-bubble-me-offer-action-focus-outline-color);
}

.message-bubble__offer-action--counter {
  background: var(--p-message-bubble-offer-action-counter-background);
  color: var(--p-message-bubble-offer-action-counter-color);
}

.message-bubble__offer-action--counter:hover {
  background: var(--p-message-bubble-offer-action-counter-hover-background);
}

.message-bubble__foot {
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: var(--p-message-bubble-foot-gap);
  font-size: var(--p-message-bubble-foot-font-size);
  color: var(--p-message-bubble-foot-color);
}

.message-bubble__receipt {
  font-size: var(--p-message-bubble-receipt-font-size);
  color: var(--p-message-bubble-receipt-color);
}
</style>
